/**
 * OverduePanel - Sliding panel showing overdue entries
 */

import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  X,
  AlertTriangle,
  Check,
  Calendar,
  Clock,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

// Format currency with proper locale
const formatCurrency = (amount, currency = 'TRY') => {
  const formatter = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

const OverduePanel = () => {
  const {
    overdueEntries,
    closeOverduePanel,
    toggleCompleted,
    rescheduleEntry,
    openEditModal,
  } = useCashflowStore();

  const handleMarkCompleted = async (entry, e) => {
    e.stopPropagation();
    await toggleCompleted(entry);
  };

  const handleReschedule = async (entry, e) => {
    e.stopPropagation();
    // Reschedule to today
    const today = format(new Date(), 'yyyy-MM-dd');
    await rescheduleEntry(entry.id, today);
  };

  const handleEntryClick = (entry) => {
    closeOverduePanel();
    openEditModal(entry);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="absolute right-0 top-0 h-full w-full max-w-md bg-gray-900 border-l border-gray-800 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Gecikmiş Ödemeler</h2>
              <p className="text-sm text-gray-400">{overdueEntries.length} adet bekleyen</p>
            </div>
          </div>
          <button
            onClick={closeOverduePanel}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3 overflow-y-auto max-h-[calc(100vh-80px)]">
          {overdueEntries.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-gray-400">Gecikmiş ödeme yok!</p>
              <p className="text-sm text-gray-500 mt-1">Tüm ödemeleriniz güncel.</p>
            </div>
          ) : (
            overdueEntries.map((entry) => {
              const isIncome = entry.type === 'income';
              const daysOverdue = formatDistanceToNow(new Date(entry.date), {
                locale: tr,
                addSuffix: true,
              });

              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4 cursor-pointer hover:bg-amber-900/30 transition-colors"
                  onClick={() => handleEntryClick(entry)}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-white font-medium">{entry.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-amber-400" />
                        <span className="text-xs text-amber-400">
                          {format(new Date(entry.date), 'd MMM yyyy', { locale: tr })} ({daysOverdue})
                        </span>
                      </div>
                    </div>
                    <p className={`text-lg font-bold ${isIncome ? 'text-emerald-400' : 'text-red-400'}`}>
                      {isIncome ? '+' : '-'}{formatCurrency(entry.amount, entry.currency)}
                    </p>
                  </div>

                  {/* Account info */}
                  {entry.account_name && (
                    <p className="text-xs text-gray-500 mb-3">
                      {entry.account_name}
                      {entry.account_type === 'credit_card' && ' (Kredi Kartı)'}
                      {entry.account_type === 'bank' && ' (Banka)'}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => handleMarkCompleted(entry, e)}
                      className="flex-1 py-2 px-3 bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-400 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Ödendi
                    </button>
                    <button
                      onClick={(e) => handleReschedule(entry, e)}
                      className="flex-1 py-2 px-3 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                    >
                      <Calendar className="w-4 h-4" />
                      Bugüne Ertele
                    </button>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Footer summary */}
        {overdueEntries.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-gray-900">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Toplam Gecikmiş</span>
              <div className="text-right">
                {/* Calculate totals by currency */}
                {(() => {
                  const totals = {};
                  overdueEntries.forEach(entry => {
                    const currency = entry.currency || 'TRY';
                    if (!totals[currency]) totals[currency] = 0;
                    totals[currency] += parseFloat(entry.amount);
                  });
                  return Object.entries(totals).map(([currency, amount]) => (
                    <p key={currency} className="text-amber-400 font-semibold">
                      {formatCurrency(amount, currency)}
                    </p>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default OverduePanel;
